"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import styles from "./request-quote.module.css";

export interface SelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "-- Select --",
  disabled = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focusIdx, setFocusIdx] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedLabel =
    options.find((o) => o.value === value)?.label ?? "";

  const filtered = search
    ? options.filter((o) =>
        o.label.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  // Reset focus index when filtered list changes
  useEffect(() => {
    setFocusIdx(-1);
  }, [search]);

  const handleSelect = useCallback(
    (val: string) => {
      onChange(val);
      setSearch("");
      setFocusIdx(-1);
      setOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange("");
    setSearch("");
    setFocusIdx(-1);
    setOpen(false);
  }, [onChange]);

  const close = useCallback(() => {
    setOpen(false);
    setSearch("");
    setFocusIdx(-1);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, close]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusIdx < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll(`[data-ss-item]`);
    items[focusIdx]?.scrollIntoView({ block: "nearest" });
  }, [focusIdx]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusIdx((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusIdx((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (focusIdx >= 0 && focusIdx < filtered.length) {
        handleSelect(filtered[focusIdx].value);
      }
    }
  };

  return (
    <div ref={wrapRef} className={styles.ssWrap} onKeyDown={handleKeyDown}>
      <button
        type="button"
        className={`${styles.ssTrigger} ${open ? styles.ssTriggerOpen : ""} ${disabled ? styles.ssTriggerDisabled : ""}`}
        onClick={() => !disabled && setOpen(!open)}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        disabled={disabled}
      >
        <span className={value ? styles.ssValue : styles.ssPlaceholder}>
          {value ? selectedLabel : placeholder}
        </span>
        <span className={styles.ssTriggerIcons}>
          {value && !disabled && (
            <i
              className={`fa-solid fa-xmark ${styles.ssClear}`}
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            />
          )}
          <i className={`fa-solid fa-chevron-down ${styles.ssChevron} ${open ? styles.ssChevronOpen : ""}`} />
        </span>
      </button>

      {open && (
        <div className={styles.ssDropdown}>
          <div className={styles.ssSearchWrap}>
            <i className={`fa-solid fa-search ${styles.ssSearchIcon}`} />
            <input
              ref={inputRef}
              type="text"
              className={styles.ssSearchInput}
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ul ref={listRef} className={styles.ssList}>
            {filtered.length === 0 && (
              <li className={styles.ssEmpty}>No results found</li>
            )}
            {filtered.map((o, i) => (
              <li
                key={o.value}
                data-ss-item
                className={`${styles.ssItem} ${o.value === value ? styles.ssItemActive : ""} ${i === focusIdx ? styles.ssItemFocused : ""}`}
                onClick={() => handleSelect(o.value)}
              >
                <span>{o.label}</span>
                {o.value === value && (
                  <i className={`fa-solid fa-check ${styles.ssCheck}`} />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
